const MAPLIBRE_VERSION = '3.5.2';
const MAPLIBRE_JS = `https://cdn.jsdelivr.net/npm/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`;
const MAPLIBRE_CSS = `https://cdn.jsdelivr.net/npm/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.css`;

const MAP_STYLE =
  'https://api.maptiler.com/maps/019c526f-fa4a-780c-8b11-02c0a0a4d684/style.json?key=nWBN7bbDiWm31ui4ps0L';
const DEFAULT_CENTER = [-47.0167469, -23.6077571];
const DEFAULT_ZOOM = 17;
const GEOLOCATION_FALLBACK_ROUTE = '/wp-json/jserrano/v1/geo-fallback';
const ADDRESS_GEOCODE_ROUTE = '/wp-json/jserrano/v1/geocode-address';
const MAX_DISTANCE_KM = 10;
const INITIAL_VISIBLE_RESULTS = 6;
const INFINITE_SCROLL_BATCH_SIZE = 6;
const INFINITE_SCROLL_LOAD_DELAY_MS = 800;

// ✅ Redução máxima de "map loads":
// - Não inicializa o mapa automaticamente.
// - Só carrega o MapLibre + cria o map quando o usuário:
//   (a) clica em "Buscar" OU (b) clica em "Ativar Geolocalização" OU (c) clica em um card.
// - Depois de carregado uma vez, reutiliza a mesma instância.

const sanitizeCep = (value = '') => value.replace(/\D/g, '');
const normalizeSearchText = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizeTypeValue = (value = '') => {
  return normalizeSearchText(value);
};

const isEcommerceTypeValue = (value = '') => {
  const normalized = normalizeTypeValue(value).replace(/[\s-]+/g, '');
  return normalized === 'ecommerce';
};

const getCardTypeValue = (card) => {
  const dataType = normalizeSearchText(card?.dataset?.representativeType || '');
  if (dataType) return dataType;

  const typeInfo = Array.from(card?.querySelectorAll('.home-locator__info') || []).find((info) => {
    const label = info.querySelector('.home-locator__info-label')?.textContent || '';
    return normalizeSearchText(label) === 'tipo';
  });

  return normalizeSearchText(typeInfo?.querySelector('.home-locator__info-value')?.textContent || '');
};

const geocodeCache = new Map(); // cep -> { lng, lat }
const addressGeocodeCache = new Map(); // normalized address -> [lng, lat]

const expandAddressAbbreviations = (address) => {
  const value = String(address || '').trim();
  if (!value) return '';

  const replacements = [
    [/(^|[\s,/-])r\.?(?=\s|,|\/|-|$)/giu, '$1rua'],
    [/(^|[\s,/-])av\.?(?=\s|,|\/|-|$)/giu, '$1avenida'],
    [/(^|[\s,/-])jd\.?(?=\s|,|\/|-|$)/giu, '$1jardim'],
    [/(^|[\s,/-])vl\.?(?=\s|,|\/|-|$)/giu, '$1vila'],
    [/(^|[\s,/-])trav\.?(?=\s|,|\/|-|$)/giu, '$1travessa'],
    [/(^|[\s,/-])rod\.?(?=\s|,|\/|-|$)/giu, '$1rodovia'],
    [/(^|[\s,/-])est\.?(?=\s|,|\/|-|$)/giu, '$1estrada'],
    [/(^|[\s,/-])al\.?(?=\s|,|\/|-|$)/giu, '$1alameda'],
    [/(^|[\s,/-])pc\.?(?=\s|,|\/|-|$)/giu, '$1praça'],
    [/(^|[\s,/-])pq\.?(?=\s|,|\/|-|$)/giu, '$1parque'],
    [/(^|[\s,/-])lot\.?(?=\s|,|\/|-|$)/giu, '$1loteamento'],
  ];

  return replacements.reduce((current, [pattern, replacement]) => current.replace(pattern, replacement), value)
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeAddressCacheKey = (address) =>
  expandAddressAbbreviations(address)
    .replace(/\s+/g, ' ')
    .toLowerCase();

const fetchCepAddress = async (cep, { signal } = {}) => {
  const clean = sanitizeCep(cep);
  if (clean.length !== 8) return null;

  const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`, { signal });
  if (!res.ok) return null;

  const data = await res.json();
  if (data?.erro) return null;

  return {
    cep: clean,
    street: data.logradouro || '',
    city: data.localidade || '',
    state: data.uf || '',
    neighborhood: data.bairro || '',
  };
};

const fetchLatLngFromAddress = async (address, { signal } = {}) => {
  if (!address) return null;

  const query = typeof address === 'string'
    ? address.trim()
    : [address.fullAddress, address.street, address.neighborhood, address.city, address.state]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(', ');

  if (!query) return null;

  const url = new URL(ADDRESS_GEOCODE_ROUTE, window.location.origin);
  url.searchParams.set('address', query);

  try {
    const response = await fetch(url.toString(), { signal });
    if (!response.ok) return null;

    const json = await response.json();
    if (!json?.success) return null;

    const lat = Number(json.latitude);
    const lng = Number(json.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    return { lat, lng };
  } catch (error) {
    if (error?.name === 'AbortError') return null;
    console.warn('[LocatorMap] Falha ao buscar coordenadas no backend:', error);
    return null;
  }
};

const normalizeAddressSegments = (address) =>
  expandAddressAbbreviations(address)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const buildAddressPayloadFromString = (address) => {
  const normalized = normalizeAddressSegments(address.trim());
  let city = '';
  let state = '';
  let streetSegments = [];

  if (normalized.length) {
    const tail = normalized[normalized.length - 1];
    const tailFragments = tail
      .split('-')
      .map((part) => part.trim())
      .filter(Boolean);

    if (tailFragments.length) {
      city = tailFragments[0];
      state = tailFragments[1] || '';
      streetSegments = normalized.slice(0, -1);
    } else {
      streetSegments = normalized;
    }
  }

  const street = streetSegments.join(', ') || address.trim();

  return {
    street,
    city,
    state,
  };
};

const getCityFromAddress = (address) => buildAddressPayloadFromString(address).city || '';

const fetchLatLngFromAddressString = async (address, options = {}) => {
  const payload = {
    ...buildAddressPayloadFromString(address),
    fullAddress: String(address || '').trim(),
  };
  return fetchLatLngFromAddress(payload, options);
};

const geocodeCepToLatLng = async (cep, { signal } = {}) => {
  const clean = sanitizeCep(cep);
  if (clean.length !== 8) return null;

  if (geocodeCache.has(clean)) return geocodeCache.get(clean);

  const address = await fetchCepAddress(clean, { signal });
  if (!address) return null;

  const coords = await fetchLatLngFromAddress(address, { signal });
  if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) return null;

  geocodeCache.set(clean, coords);
  return coords;
};

const toRad = (n) => (n * Math.PI) / 180;
const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(x));
};

const ensureMapLibreCss = () => {
  if (document.querySelector(`link[href="${MAPLIBRE_CSS}"]`)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = MAPLIBRE_CSS;
  document.head.appendChild(link);
};

let mapLibreLoader;

const ensureMapLibre = () => {
  if (mapLibreLoader) return mapLibreLoader;

  ensureMapLibreCss();

  mapLibreLoader = new Promise((resolve, reject) => {
    const normalize = (ml) => {
      if (!ml) return null;
      if (ml.default && (ml.default.Map || ml.default.supported)) return ml.default;
      return ml;
    };

    const tryResolve = () => {
      const normalized = normalize(window.maplibregl);
      if (normalized && typeof normalized.Map === 'function') {
        resolve(normalized);
        return true;
      }
      return false;
    };

    if (tryResolve()) return;

    const script = document.createElement('script');
    script.src = MAPLIBRE_JS;

    script.onload = () => {
      if (!tryResolve()) {
        reject(new Error('MapLibre carregou, mas window.maplibregl não é válido (sem Map).'));
      }
    };

    script.onerror = (e) => {
      console.error('[LocatorMap] Erro ao baixar MapLibre:', e);
      reject(new Error(`Failed to load MapLibre GL JS: ${MAPLIBRE_JS}`));
    };

    document.head.appendChild(script);
  });

  return mapLibreLoader;
};

const setButtonState = (button, { loading }) => {
  if (!button) return;

  button.disabled = loading;

  if (loading) {
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
  } else {
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
  }
};

const requestCurrentPosition = (options = {}) =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocalização não suportada'));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 600000,
      ...options,
    });
  });

const requestBestCurrentPosition = async () => {
  try {
    return await requestCurrentPosition();
  } catch (error) {
    if (error?.code !== 3 && error?.name !== 'TimeoutError') {
      throw error;
    }

    return await requestCurrentPosition({
      enableHighAccuracy: false,
      timeout: 20000,
      maximumAge: 300000,
    });
  }
};

const applyGeolocation = async (map, marker, button, positionPromise = null) => {
  const setMarkerAt = (coords) => {
    marker?.setLngLat(coords);
    map.flyTo({ center: coords, zoom: 13 });
    return coords;
  };

  const handleFallback = async (reason) => {
    console.warn('[LocatorMap] Geolocalização principal falhou:', reason);
    try {
      const response = await fetch(GEOLOCATION_FALLBACK_ROUTE);
      if (!response.ok) throw new Error('fallback retornou status ' + response.status);
      const json = await response.json();
      if (json?.latitude && json?.longitude) {
        const fallbackCoords = [Number(json.longitude), Number(json.latitude)];
        console.log('[LocatorMap] fallback geocode:', fallbackCoords);
        return setMarkerAt(fallbackCoords);
      }
      throw new Error('fallback não retornou coordenadas válidas');
    } catch (fallbackError) {
      console.warn('[LocatorMap] fallback de geolocalização falhou:', fallbackError);
      return setMarkerAt(DEFAULT_CENTER);
    }
  };

  setButtonState(button, { loading: true });

  try {
    const position = positionPromise || requestBestCurrentPosition();
    const resolvedPosition = await position;
    const coords = [resolvedPosition.coords.longitude, resolvedPosition.coords.latitude];
    console.log('[LocatorMap] Usando geolocalização nativa:', coords);
    setMarkerAt(coords);

    return {
      coords,
      approximate: false,
    };
  } catch (error) {
    button?.setAttribute('aria-live', 'polite');
    const fallbackCoords = await handleFallback(error);

    return {
      coords: fallbackCoords,
      approximate: true,
    };
  } finally {
    setButtonState(button, { loading: false });
  }
};

const geocodeAddress = async (address) => {
  if (!address) return null;
  const cacheKey = normalizeAddressCacheKey(address);
  if (addressGeocodeCache.has(cacheKey)) {
    return addressGeocodeCache.get(cacheKey);
  }

  const url = new URL(ADDRESS_GEOCODE_ROUTE, window.location.origin);
  url.searchParams.set('address', expandAddressAbbreviations(address));

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('status ' + response.status);
    const json = await response.json();
    if (!json?.success) throw new Error(json?.message || 'geocode failed');
    const lng = Number(json.longitude);
    const lat = Number(json.latitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      throw new Error('invalid geocode coords');
    }
    const coords = [lng, lat];
    addressGeocodeCache.set(cacheKey, coords);
    return coords;
  } catch (error) {
    console.warn('[LocatorMap] geocode address failed (server):', address, error);
  }

  return null;
};

const setupMapScrollIsolation = (map, containerEl) => {
  if (!map || !containerEl) return;
  if (containerEl.dataset.mapScrollIsolation === 'enabled') return;

  const canvas = map.getCanvas();
  if (!canvas) return;

  containerEl.dataset.mapScrollIsolation = 'enabled';

  const eventWithinMap = (event) => {
    const target = event.target;
    return target instanceof Element && containerEl.contains(target);
  };

  const preventPageWheel = (event) => {
    if (!event.cancelable || !eventWithinMap(event)) return;
    event.preventDefault(); // passive:false permite bloquear o scroll da página enquanto mantém o zoom do map.
  };

  const preventSingleTouchDrag = (event) => {
    if (!event.cancelable || event.touches?.length !== 1 || !eventWithinMap(event)) return;
    event.preventDefault();
  };

  document.addEventListener('wheel', preventPageWheel, { passive: false, capture: true });
  document.addEventListener('touchmove', preventSingleTouchDrag, { passive: false, capture: true });

  const stopLenis = () => {
    if (window.lenis?.stop) window.lenis.stop();
  };

  const startLenis = () => {
    if (window.lenis?.start) window.lenis.start();
  };

  [containerEl, canvas].forEach((element) => {
    element.addEventListener('pointerenter', stopLenis);
    element.addEventListener('pointerleave', startLenis);
  });
};

const setupZoomControls = (map, containerEl) => {
  if (!map || !containerEl) return;
  const mapWrapper = containerEl.querySelector('.home-locator__map-inner') || containerEl;
  if (mapWrapper.dataset.zoomControls === 'true') return;

  const createButton = (label, symbol, handler, modifier) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `home-locator__zoom-button home-locator__zoom-button--${modifier}`;
    button.setAttribute('aria-label', label);
    const span = document.createElement('span');
    span.className = 'home-locator__zoom-symbol';
    span.textContent = symbol;
    button.appendChild(span);
    button.addEventListener('click', (event) => {
      event.preventDefault();
      handler();
    });
    return button;
  };

  const controls = document.createElement('div');
  controls.className = 'home-locator__zoom-controls';
  controls.append(
    createButton('Dar zoom no mapa', '+', () => map.zoomIn(), 'in'),
    createButton('Reduzir zoom do mapa', '−', () => map.zoomOut(), 'out')
  );

  mapWrapper.appendChild(controls);
  mapWrapper.dataset.zoomControls = 'true';
};

export const initLocatorMap = () => {
  const container = document.querySelector('[data-home-locator-map]');
  if (!container) return;

  const mapInner = container.querySelector('.home-locator__map-inner');
  const locatorSection = container.closest('.home-locator');

  const showError = (message) => {
    container.classList.add('home-locator__map--error');
    if (mapInner) {
      mapInner.textContent = message;
      return;
    }
    container.textContent = message;
  };
  const mapWrapper = container.closest('.home-locator__map');

  const scrollToMapAnchor = () => {
    const mapAnchor =
      document.getElementById('home-locator-map') || container.closest('.home-locator__map');
    if (!mapAnchor) return;
    requestAnimationFrame(() => {
      mapAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const form = document.querySelector('.home-locator__search');
  const resultCards = Array.from(document.querySelectorAll('.home-locator__card'));
  const resultsGrid = document.querySelector('.home-locator__results-grid');
  const resultsCountEl = document.querySelector('[data-home-locator-results-count]');
  const resultsFooterEl = document.querySelector('[data-home-locator-results-footer]');
  const resultsFooterTextEl = document.querySelector('[data-home-locator-results-footer-text]');
  const resultsCountTemplate =
    resultsCountEl?.dataset?.homeLocatorResultsCountTemplate || 'Encontramos %s resultados';
  const infiniteScrollEnabled = locatorSection?.dataset.homeLocatorInfinite === 'true';

  let mapInstance = null;
  let maplibreglModule = null;
  let selectionMarker = null;
  let resultMarkers = [];
  let searchAbortController = null;
  let lastVisibleIndexes = [];
  let infiniteScrollObserver = null;
  let infiniteScrollSentinel = null;
  let infiniteScrollIsLoading = false;
  let infiniteScrollLoadTimer = null;

  const setMapVisibility = (show) => {
    if (!mapWrapper) return;
    mapWrapper.classList.toggle('home-locator__map--hidden', !show);
    if (show && mapInstance && maplibreglModule) {
      createResultMarkers();
    }
  };

  const setResultsCount = (count) => {
    if (!resultsCountEl) return;
    const text = resultsCountTemplate.replace('%s', count);
    resultsCountEl.textContent = text;
  };

  const updateInfiniteScrollFooter = (visibleCount, totalCount, isLoading = false) => {
    if (!resultsFooterEl) return;

    const shouldShow = infiniteScrollEnabled && totalCount > 0;
    const hasMoreResults = visibleCount < totalCount;
    resultsFooterEl.hidden = !shouldShow;
    resultsFooterEl.classList.toggle('is-loading', Boolean(shouldShow && isLoading && hasMoreResults));

    if (resultsFooterTextEl) {
      resultsFooterTextEl.textContent = hasMoreResults
        ? 'Carregando mais resultados'
        : 'Não tem mais resultado';
    }
  };

  const clearInfiniteScrollLoadTimer = () => {
    if (infiniteScrollLoadTimer) {
      window.clearTimeout(infiniteScrollLoadTimer);
      infiniteScrollLoadTimer = null;
    }
  };

  const reorderCardsByIndexes = (indexes) => {
    if (!resultsGrid || !Array.isArray(indexes)) return;
    orderedResultIndexes = indexes.slice();
    indexes.forEach((idx) => {
      const card = results[idx]?.card;
      if (card) {
        resultsGrid.appendChild(card);
      }
    });
  };

  const getSortedIndexesWithDistance = ({ lng, lat }, indexes) => {
    if (!Array.isArray(indexes)) return [];
    return [...indexes]
      .map((idx) => {
        const result = results[idx];
        if (!result || typeof result.latitude !== 'number' || typeof result.longitude !== 'number') {
          return null;
        }
        return {
          idx,
          dist: haversineKm({ lat, lng }, { lat: result.latitude, lng: result.longitude }),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.dist - b.dist);
  };

  const parseNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const results = resultCards.map((card) => {
    const address = (card.dataset.address || '').trim();
    return {
      card,
      latitude: parseNumber(card.dataset.latitude),
      longitude: parseNumber(card.dataset.longitude),
      products: (card.dataset.products || '')
        .split(',')
        .map((value) => normalizeSearchText(value))
        .filter(Boolean),
      address,
      city: getCityFromAddress(address),
    };
  });

  let visibleLoadLimit = infiniteScrollEnabled ? INITIAL_VISIBLE_RESULTS : results.length;
  let orderedResultIndexes = results.map((_, index) => index);

  let cityMarkerIndexes = null;

  const getIndexesByCity = (city) => {
    if (!city) return [];
    const normalizedCity = city.toLowerCase();
    return results
      .map((result, index) => ({ result, index }))
      .filter(
        ({ result }) => result.city && result.city.toLowerCase() === normalizedCity
      )
      .map(({ index }) => index);
  };

  const ensureAddressCoordinates = async () => {
    const pending = results
      .map((result, index) => {
        const hasLatitude = typeof result.latitude === 'number';
        const hasLongitude = typeof result.longitude === 'number';
        if (
          (hasLatitude && hasLongitude) ||
          !result.address
        ) {
          return null;
        }
        return { index, address: result.address };
      })
      .filter(Boolean);

    if (!pending.length) {
      return;
    }

    for (const { index, address } of pending) {
      const coords = await geocodeAddress(address);
      if (!coords) continue;

      const [lng, lat] = coords;
      results[index].latitude = lat;
      results[index].longitude = lng;
      results[index].card.dataset.latitude = lat;
      results[index].card.dataset.longitude = lng;
    }
  };

  const addressCoordsReady = ensureAddressCoordinates();

  const ensureInfiniteScrollSentinel = () => {
    if (!infiniteScrollEnabled || !resultsGrid) {
      return null;
    }

    if (infiniteScrollSentinel) {
      return infiniteScrollSentinel;
    }

    infiniteScrollSentinel = document.createElement('div');
    infiniteScrollSentinel.className = 'home-locator__results-sentinel';
    infiniteScrollSentinel.setAttribute('aria-hidden', 'true');
    resultsGrid.insertAdjacentElement('afterend', infiniteScrollSentinel);

    return infiniteScrollSentinel;
  };

  const stopInfiniteScrollObserver = () => {
    if (infiniteScrollObserver) {
      infiniteScrollObserver.disconnect();
      infiniteScrollObserver = null;
    }
    infiniteScrollIsLoading = false;
    clearInfiniteScrollLoadTimer();
  };

  const updateResultVisibility = (
    visibleIndexes = null,
    { resetInfiniteScrollState = false, preserveOrderedIndexes = false } = {}
  ) => {
    let finalIndexes;
    let visibleCount;
    const shouldUseInfinitePaging = infiniteScrollEnabled;
    const nextIndexes = Array.isArray(visibleIndexes)
      ? visibleIndexes.slice()
      : results.map((_, index) => index);

    if (!preserveOrderedIndexes) {
      orderedResultIndexes = nextIndexes.slice();
    }

    if (shouldUseInfinitePaging) {
      if (resetInfiniteScrollState) {
        infiniteScrollIsLoading = false;
        clearInfiniteScrollLoadTimer();
      }

      const nextLimit = resetInfiniteScrollState
        ? INITIAL_VISIBLE_RESULTS
        : visibleLoadLimit;
      visibleLoadLimit = Math.min(nextLimit, orderedResultIndexes.length);
    }

    const indexesToShow = shouldUseInfinitePaging
      ? orderedResultIndexes.slice(0, visibleLoadLimit)
      : nextIndexes;
    const visibleSet = new Set(indexesToShow);
    let shown = 0;
    resultCards.forEach((card, idx) => {
      const isVisible = visibleSet.has(idx);
      card.classList.toggle('is-hidden', !isVisible);
      if (isVisible) shown += 1;
    });
    setResultsCount(shown);
    finalIndexes = indexesToShow.slice();
    visibleCount = shown;
    updateInfiniteScrollFooter(visibleCount, orderedResultIndexes.length);

    lastVisibleIndexes = Array.isArray(finalIndexes) ? finalIndexes : [];
    createResultMarkers(lastVisibleIndexes);

    if (activeResultIndex !== null) {
      const activeCard = results[activeResultIndex]?.card;
      const activeIsVisible = finalIndexes.includes(activeResultIndex);
      if (activeCard && activeIsVisible) {
        clearActiveCard();
        activeCard.classList.add('is-active');
      } else if (activeCard) {
        activeCard.classList.remove('is-active');
      }
    }

    syncInfiniteScrollObserver();
    return visibleCount;
  };

  const syncInfiniteScrollObserver = () => {
    if (!infiniteScrollEnabled) {
      updateInfiniteScrollFooter(lastVisibleIndexes.length, orderedResultIndexes.length);
      return;
    }

    const sentinel = ensureInfiniteScrollSentinel();
    if (!sentinel) {
      updateInfiniteScrollFooter(lastVisibleIndexes.length, orderedResultIndexes.length);
      return;
    }

    if (!infiniteScrollObserver) {
      if (!('IntersectionObserver' in window)) {
        return;
      }

      infiniteScrollObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            return;
          }

          if (visibleLoadLimit >= orderedResultIndexes.length) {
            stopInfiniteScrollObserver();
            infiniteScrollIsLoading = false;
            clearInfiniteScrollLoadTimer();
            updateInfiniteScrollFooter(orderedResultIndexes.length, orderedResultIndexes.length);
            return;
          }

          if (infiniteScrollIsLoading) {
            return;
          }

          infiniteScrollIsLoading = true;
          updateInfiniteScrollFooter(visibleLoadLimit, orderedResultIndexes.length, true);
          clearInfiniteScrollLoadTimer();
          infiniteScrollLoadTimer = window.setTimeout(() => {
            visibleLoadLimit = Math.min(
              visibleLoadLimit + INFINITE_SCROLL_BATCH_SIZE,
              orderedResultIndexes.length
            );
            updateResultVisibility(orderedResultIndexes);
            infiniteScrollIsLoading = false;
            infiniteScrollLoadTimer = null;
          }, INFINITE_SCROLL_LOAD_DELAY_MS);
        },
        {
          root: null,
          rootMargin: '240px 0px',
          threshold: 0,
        }
      );
    }

    infiniteScrollObserver.disconnect();

    if (visibleLoadLimit < orderedResultIndexes.length) {
      infiniteScrollObserver.observe(sentinel);
      updateInfiniteScrollFooter(visibleLoadLimit, orderedResultIndexes.length);
    } else {
      updateInfiniteScrollFooter(orderedResultIndexes.length, orderedResultIndexes.length);
    }
  };

  let activeResultIndex = null;

  const clearActiveCard = () => {
    resultCards.forEach((card) => card.classList.remove('is-active'));
  };

  const ensureResultCoordinates = async (index) => {
    const target = results[index];
    if (!target) return null;

    if (
      typeof target.latitude === 'number' &&
      typeof target.longitude === 'number'
    ) {
      return [target.longitude, target.latitude];
    }

    if (!target.address) {
      return null;
    }

    const coords = await geocodeAddress(target.address);
    if (!coords) {
      return null;
    }

    const [lng, lat] = coords;
    results[index].latitude = lat;
    results[index].longitude = lng;
    results[index].card.dataset.latitude = lat;
    results[index].card.dataset.longitude = lng;
    createResultMarkers(lastVisibleIndexes);
    return coords;
  };

  const focusResult = async (index) => {
    const target = results[index];
    if (!target) return;

    let coords = [DEFAULT_CENTER[0], DEFAULT_CENTER[1]];
    const resolvedCoords = await ensureResultCoordinates(index);
    if (Array.isArray(resolvedCoords)) {
      coords = resolvedCoords;
    }

    clearActiveCard();
    target.card.classList.add('is-active');
    activeResultIndex = index;

    if (!mapInstance || !selectionMarker) return;

    selectionMarker.setLngLat(coords);
    mapInstance.flyTo({ center: coords, zoom: 12 });
  };

  const getFilteredIndexes = ({ productValue, representativeTypeValue }) => {
    const normalizedProductValue = normalizeSearchText(productValue);
    const normalizedRepresentativeTypeValue = normalizeTypeValue(representativeTypeValue);

    return results
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => {
        const cardType = getCardTypeValue(r.card);

        const matchesProduct = normalizedProductValue
          ? r.products.some((p) => p === normalizedProductValue)
          : true;

        const matchesType = normalizedRepresentativeTypeValue
          ? Boolean(cardType) &&
            (cardType === normalizedRepresentativeTypeValue ||
              cardType.includes(normalizedRepresentativeTypeValue))
          : true;

        return matchesProduct && matchesType;
      })
      .map(({ idx }) => idx);
  };

  const focusNearestResultToPoint = ({ lng, lat }, candidateIndexes) => {
    if (!mapInstance || !selectionMarker) return;

    const withCoords = candidateIndexes
      .map((idx) => {
        const r = results[idx];
        if (typeof r.latitude !== 'number' || typeof r.longitude !== 'number') return null;

        return {
          idx,
          dist: haversineKm({ lat, lng }, { lat: r.latitude, lng: r.longitude }),
        };
      })
      .filter(Boolean);

    if (!withCoords.length) {
      selectionMarker.setLngLat([lng, lat]);
      mapInstance.flyTo({ center: [lng, lat], zoom: 12 });
      clearActiveCard();
      activeResultIndex = null;
      return;
    }

    withCoords.sort((a, b) => a.dist - b.dist);
    void focusResult(withCoords[0].idx);
  };

  const createResultMarkers = (indexes = lastVisibleIndexes) => {
    if (!mapInstance || !maplibreglModule) return;
    if (mapWrapper?.classList.contains('home-locator__map--hidden')) return;

    const baseIndexes = Array.isArray(indexes) ? indexes : results.map((_, index) => index);
    const combinedIndexes = new Set(baseIndexes);
    if (Array.isArray(cityMarkerIndexes) && cityMarkerIndexes.length) {
      cityMarkerIndexes.forEach((idx) => combinedIndexes.add(idx));
    }
    const indexesToShow = combinedIndexes.size ? Array.from(combinedIndexes) : [];

    resultMarkers.forEach((marker) => {
      marker?.remove();
    });
    resultMarkers = [];

    indexesToShow.forEach((idx) => {
      const result = results[idx];
      if (!result || typeof result.latitude !== 'number' || typeof result.longitude !== 'number') return;

      const markerEl = document.createElement('button');
      markerEl.type = 'button';
      markerEl.className = 'home-locator__map-marker';
      const label = result.company || result.location || `Representante ${idx + 1}`;
      markerEl.setAttribute('aria-label', `Ir para ${label}`);
      markerEl.addEventListener('click', () => focusResult(idx));

      const marker = new maplibreglModule.Marker(markerEl)
        .setLngLat([result.longitude, result.latitude])
        .addTo(mapInstance);

      resultMarkers.push(marker);
    });
  };

  setMapVisibility(true);

  // ✅ Inicialização sob demanda (economiza map loads)
  let mapInitPromise = null;

  const initMapIfNeeded = () => {
    if (mapInitPromise) return mapInitPromise;

    // aqui você pode trocar o placeholder por "Carregando mapa..."
    if (mapInner) mapInner.textContent = 'Carregando mapa...';

    mapInitPromise = ensureMapLibre()
      .then(async (maplibregl) => {
        await addressCoordsReady;
        if (typeof maplibregl.supported === 'function' && !maplibregl.supported()) {
          showError('Seu navegador não suporta mapas WebGL.');
          throw new Error('WebGL not supported');
        }

        mapInstance = new maplibregl.Map({
          container: mapInner || container,
          style: MAP_STYLE,
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          attributionControl: false,
          interactive: true,
          maxZoom: 15, // reduz um pouco o "tile churn"
        });

        selectionMarker = new maplibregl.Marker({ color: '#ce542b' })
          .setLngLat(DEFAULT_CENTER)
          .addTo(mapInstance);
        maplibreglModule = maplibregl;

        setupMapScrollIsolation(mapInstance, mapInner || container);
        setupZoomControls(mapInstance, container);

        mapInstance.on('load', () => {
          mapInstance.resize();
          createResultMarkers();
        });

        mapInstance.on('error', (e) => {
          console.error('[LocatorMap] Map error:', e?.error || e);
        });

        return mapInstance;
      })
      .catch((err) => {
        console.error('[LocatorMap] Falha ao carregar MapLibre:', err);
        showError(`Não foi possível carregar o mapa. (${err?.message || 'erro desconhecido'})`);
        throw err;
      });

    return mapInitPromise;
  };

  const focusInitialResult = () => {
    if (!results.length) {
      return;
    }
    void focusResult(0);
  };

  const handleFormSearch = async (event) => {
    event.preventDefault();
    cityMarkerIndexes = null;

    const cepRaw = form?.querySelector('input[name="cep"]')?.value.trim() || '';
    const cepValue = sanitizeCep(cepRaw);

    const productValue =
      form?.querySelector('select[name="product"]')?.value.trim() || '';
    const representativeTypeValue =
      form?.querySelector('select[name="representative_type"]')?.value.trim() || '';

    const candidateIndexes = getFilteredIndexes({ productValue, representativeTypeValue });
    const shouldHideMapForEcommerce = isEcommerceTypeValue(representativeTypeValue);

    if (shouldHideMapForEcommerce) {
      setMapVisibility(false);
      updateResultVisibility(candidateIndexes, { resetInfiniteScrollState: true });
      clearActiveCard();
      activeResultIndex = null;
      return;
    }

    // ✅ só inicializa mapa quando o usuário realmente clicar em Buscar
    await initMapIfNeeded().catch(() => null);
    if (!mapInstance || !selectionMarker) return;

    const hasFilter = Boolean(cepValue || productValue || representativeTypeValue);
    if (!hasFilter) {
      setMapVisibility(true);
      updateResultVisibility(candidateIndexes, { resetInfiniteScrollState: true });
      return;
    }
    setMapVisibility(true);
    if (!cepValue) {
      updateResultVisibility(candidateIndexes, { resetInfiniteScrollState: true });
    }

    if (cepValue) {
      searchAbortController?.abort();
      searchAbortController = new AbortController();

      try {
        const coords = await geocodeCepToLatLng(cepValue, {
          signal: searchAbortController.signal,
        });

        if (!coords) {
          await focusResult(candidateIndexes[0] ?? 0);
          return;
        }

        const effectiveBaseIndexes =
          candidateIndexes.length > 0 ? candidateIndexes : results.map((_, i) => i);
        const sortedWithDistance = getSortedIndexesWithDistance(
          { lng: coords.lng, lat: coords.lat },
          effectiveBaseIndexes
        );
        const withinRadius = sortedWithDistance
          .filter(({ dist }) => dist <= MAX_DISTANCE_KM)
          .map(({ idx }) => idx);
        if (!withinRadius.length) {
          updateResultVisibility([], { resetInfiniteScrollState: true });
          setMapVisibility(false);
          return;
        }

        updateResultVisibility(withinRadius, { resetInfiniteScrollState: true });
        reorderCardsByIndexes(withinRadius);
        const effectiveIndexes = withinRadius;

        selectionMarker.setLngLat([coords.lng, coords.lat]);
        mapInstance.flyTo({ center: [coords.lng, coords.lat], zoom: 12 });

        focusNearestResultToPoint({ lng: coords.lng, lat: coords.lat }, effectiveIndexes);

        return;
      } catch (e) {
        if (e?.name === 'AbortError') return;
        await focusResult(candidateIndexes[0] ?? 0);
        return;
      }
    }

    await focusResult(candidateIndexes[0] ?? 0);
  };

  // ✅ Card click agora também carrega mapa sob demanda
  resultCards.forEach((card, index) => {
    card.addEventListener('click', async () => {
      const representativeTypeValue =
        form?.querySelector('select[name="representative_type"]')?.value.trim() || '';
      if (isEcommerceTypeValue(representativeTypeValue)) {
        return;
      }

      await initMapIfNeeded().catch(() => null);
      const city = results[index]?.city;
      cityMarkerIndexes = city ? getIndexesByCity(city) : null;
      setMapVisibility(true);
      createResultMarkers();
      await focusResult(index);
      scrollToMapAnchor();
    });
  });

  form?.addEventListener('submit', handleFormSearch);
  form?.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) {
      return;
    }

    if (target.name !== 'product' && target.name !== 'representative_type') {
      return;
    }

    void handleFormSearch(new Event('submit', { cancelable: true }));
  });

  // ✅ Geolocalização também inicializa sob demanda
  const geolocateButton = document.querySelector('[data-home-locator-geolocate]');
  const shouldLimitInitialResults =
    document.querySelector('[data-home-locator-limit-items="false"]') === null;

  geolocateButton?.addEventListener('click', async () => {
    cityMarkerIndexes = null;
    const positionPromise = requestBestCurrentPosition();
    positionPromise.catch(() => null);
    await initMapIfNeeded().catch(() => null);
    if (!mapInstance || !selectionMarker) return;

    const location = await applyGeolocation(
      mapInstance,
      selectionMarker,
      geolocateButton,
      positionPromise
    );

    const coords = location?.coords;
    if (coords) {
      focusNearestResultToPoint(
        { lng: coords[0], lat: coords[1] },
        results.map((_, index) => index)
      );
    }
    clearActiveCard();
    activeResultIndex = null;
  });

  initMapIfNeeded()
    .then(() => {
      focusInitialResult();
    })
    .catch(() => null);

  if (!shouldLimitInitialResults) {
    updateResultVisibility(orderedResultIndexes, {
      resetInfiniteScrollState: true,
      preserveOrderedIndexes: true,
    });
  } else {
    updateResultVisibility(results.map((_, index) => index).slice(0, INITIAL_VISIBLE_RESULTS), {
      resetInfiniteScrollState: true,
      preserveOrderedIndexes: true,
    });
  }

};
